/**
 * @fileoverview MessageFeedbackBar — "Did this answer your question?"
 * row that follows an assistant message. Two small minimal thumbs-up /
 * thumbs-down buttons, both wired to popover targets in Foundry for an
 * optional follow-up free-form feedback form. Owner controls the rating.
 *
 * Foundry mapping: `user-feedback__feedback-container` +
 * `__feedback-simple` with `bp6-text-muted bp6-text-small` prompt text
 * and two minimal small `thumbs-up` / `thumbs-down` Buttons.
 */

import type { ReactElement } from "react";
import { Button, Intent } from "@blueprintjs/core";

export type FeedbackRating = "up" | "down" | undefined;

interface Props {
  rating: FeedbackRating;
  onRate: (rating: FeedbackRating) => void;
  prompt?: string;
  className?: string;
}

export function MessageFeedbackBar({
  rating,
  onRate,
  prompt = "Did this answer your question?",
  className,
}: Props): ReactElement {
  return (
    <div data-testid="message-feedback-bar" className={["bp-feedback-bar", className].filter(Boolean).join(" ")}>
      <div className="bp6-text-muted bp6-text-small">{prompt}</div>
      <div>
        <Button
          data-testid="message-feedback-bar-up"
          small
          minimal
          icon="thumbs-up"
          aria-label="Thumbs up"
          intent={rating === "up" ? Intent.SUCCESS : Intent.NONE}
          onClick={() => onRate(rating === "up" ? undefined : "up")}
        />
        <Button
          data-testid="message-feedback-bar-down"
          small
          minimal
          icon="thumbs-down"
          aria-label="Thumbs down"
          intent={rating === "down" ? Intent.DANGER : Intent.NONE}
          onClick={() => onRate(rating === "down" ? undefined : "down")}
        />
      </div>
    </div>
  );
}
